import { useCallback, useEffect, useState } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { reportMonthToDate, runReport, type ReportResult } from '../lib/reports';

/**
 * Marketing performance for the Control Centre, read from the report
 * system: `campaigns` (email and text blasts) and `social` (connected
 * Facebook, Instagram and LinkedIn accounts). This month so far against
 * the same days of last month.
 */

export type SocialPlatform = 'facebook' | 'instagram' | 'linkedin';

export interface ChannelFigures {
  campaigns: number;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  unsubscribed: number;
  openRate: number;
  clickRate: number;
}

export interface SocialFigures {
  platform: SocialPlatform;
  account: string | null;
  posts: number;
  likes: number;
  comments: number;
  engagement: number;
  reach: number;
  followers: number;
  newFollowers: number | null;
  accountReach: number;
}

export interface MarketingReports {
  loading: boolean;
  error: string | null;
  /** Email blasts, when the business has ever sent one. */
  email: { current: ChannelFigures; previous: ChannelFigures } | null;
  /** Text blasts, when the business has ever sent one. */
  sms: { current: ChannelFigures; previous: ChannelFigures } | null;
  /** One entry per connected social platform, in display order. */
  social: Array<{ current: SocialFigures; previous: SocialFigures }>;
  window: { since: string; until: string };
  refresh: () => Promise<void>;
}

const PLATFORM_ORDER: SocialPlatform[] = ['facebook', 'instagram', 'linkedin'];
const CAMPAIGN_METRICS = 'campaigns, sent, delivered, opened, clicked, unsubscribed, open_rate, click_rate';
const SOCIAL_METRICS = 'posts, likes, comments, engagement, reach, followers, new_followers, account_reach';

function num(row: Record<string, string | number> | undefined, key: string): number {
  const raw = row?.[key];
  const n = typeof raw === 'number' ? raw : Number(raw ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function emptyChannel(): ChannelFigures {
  return { campaigns: 0, sent: 0, delivered: 0, opened: 0, clicked: 0, unsubscribed: 0, openRate: 0, clickRate: 0 };
}

function channelFrom(result: ReportResult, channel: 'email' | 'sms'): ChannelFigures {
  const row = result.rows.find((r) => String(r.channel) === channel);
  if (!row) return emptyChannel();
  return {
    campaigns: num(row, 'campaigns'),
    sent: num(row, 'sent'),
    delivered: num(row, 'delivered'),
    opened: num(row, 'opened'),
    clicked: num(row, 'clicked'),
    unsubscribed: num(row, 'unsubscribed'),
    openRate: num(row, 'open_rate'),
    clickRate: num(row, 'click_rate'),
  };
}

function socialFrom(
  result: ReportResult,
  platform: SocialPlatform,
  account: string | null,
): SocialFigures {
  const row = result.rows.find((r) => String(r.platform) === platform);
  const newRaw = row?.new_followers;
  return {
    platform,
    account,
    posts: num(row, 'posts'),
    likes: num(row, 'likes'),
    comments: num(row, 'comments'),
    engagement: num(row, 'engagement'),
    reach: num(row, 'reach'),
    followers: num(row, 'followers'),
    newFollowers: newRaw == null || newRaw === '' ? null : num(row, 'new_followers'),
    accountReach: num(row, 'account_reach'),
  };
}

export function useControlCentreMarketingReports(enabled: boolean): MarketingReports {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const timeZone = selectedBusiness?.timezone || 'America/Vancouver';

  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<MarketingReports['email']>(null);
  const [sms, setSms] = useState<MarketingReports['sms']>(null);
  const [social, setSocial] = useState<MarketingReports['social']>([]);
  const [window, setWindow] = useState({ since: '', until: '' });

  const refresh = useCallback(async () => {
    if (!enabled || !businessId) {
      setEmail(null);
      setSms(null);
      setSocial([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const current = reportMonthToDate(timeZone, 0);
      const previous = reportMonthToDate(timeZone, -1);
      setWindow(current);
      const cur = `SINCE ${current.since} UNTIL ${current.until}`;
      const prev = `SINCE ${previous.since} UNTIL ${previous.until}`;

      const [connections, everSent, campaignsNow, campaignsBefore] = await Promise.all([
        supabase
          .from('social_media_connections')
          .select('platform, display_name, username')
          .eq('business_id', businessId)
          .eq('is_active', true),
        // Which channels this business actually uses decides which cards show.
        runReport(businessId, `FROM campaigns SHOW campaigns GROUP BY channel SINCE 2020-01-01 UNTIL ${current.until}`),
        runReport(businessId, `FROM campaigns SHOW ${CAMPAIGN_METRICS} GROUP BY channel ${cur}`),
        runReport(businessId, `FROM campaigns SHOW ${CAMPAIGN_METRICS} GROUP BY channel ${prev}`),
      ]);
      if (connections.error) throw new Error(connections.error.message);

      const usesEmail = everSent.rows.some((r) => String(r.channel) === 'email' && num(r, 'campaigns') > 0);
      const usesSms = everSent.rows.some((r) => String(r.channel) === 'sms' && num(r, 'campaigns') > 0);
      setEmail(usesEmail ? { current: channelFrom(campaignsNow, 'email'), previous: channelFrom(campaignsBefore, 'email') } : null);
      setSms(usesSms ? { current: channelFrom(campaignsNow, 'sms'), previous: channelFrom(campaignsBefore, 'sms') } : null);

      const accounts = new Map<SocialPlatform, string | null>();
      for (const row of connections.data || []) {
        const platform = String(row.platform) as SocialPlatform;
        if (!PLATFORM_ORDER.includes(platform) || accounts.has(platform)) continue;
        accounts.set(platform, (row.display_name as string | null) || (row.username as string | null) || null);
      }

      if (accounts.size === 0) {
        setSocial([]);
      } else {
        const [socialNow, socialBefore] = await Promise.all([
          runReport(businessId, `FROM social SHOW ${SOCIAL_METRICS} GROUP BY platform ${cur}`),
          runReport(businessId, `FROM social SHOW ${SOCIAL_METRICS} GROUP BY platform ${prev}`),
        ]);
        setSocial(
          PLATFORM_ORDER.filter((p) => accounts.has(p)).map((platform) => ({
            current: socialFrom(socialNow, platform, accounts.get(platform) ?? null),
            previous: socialFrom(socialBefore, platform, accounts.get(platform) ?? null),
          })),
        );
      }
    } catch (err) {
      console.error('[useControlCentreMarketingReports]', err);
      setError(err instanceof Error ? err.message : 'Could not load marketing reports.');
      setEmail(null);
      setSms(null);
      setSocial([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, businessId, timeZone]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { loading, error, email, sms, social, window, refresh };
}
